import { isMobile } from 'mobile-device-detect';

import { EventBus } from '@/helpers/event-bus';
import { isNativeApp } from '@/helpers/platform-helper';
import { ISignCommand } from '@/sign/commands/isign.command';
import { ReadKeyCommand } from '@/sign/commands/read-key.command';
import { SignCommand } from '@/sign/commands/sign.command';
import { SignSettings } from '@/sign/models/sign-settings.model';
import { SignMethod, SignPurpose } from '@/sign/models/sign-constants';
import { SignTextStrategyFactory } from '@/sign/factories/sign-text-strategy.factory';
import { SignFilesStrategyFactory } from '@/sign/factories/sign-files-strategy.factory';
import { ConfirmDocumentReceiptStrategyFactory } from '@/sign/factories/confirm-document-receipt-strategy.factory';
import { SignResult } from '@/sign/models/sign-result.model';
import { SignDocumentAuthorCommand } from '../commands/sign-document-author.command';
import { SignDocumentContragentCommand } from '../commands/sign-document-contragent.command';
import { SignSignaturesRecallProtocolCommand } from '../commands/sign-signatures-recall-protocol.command';
import { isCancelledError } from '@/helpers/error-helper';

class SignService {
  private signCommand: ISignCommand | undefined;
  private resolveSignFn: (value?: any) => void = () => { };
  private signSettings: SignSettings;
  private lastSigningPrms?: any;

  constructor() {
    this.signSettings = this.getDefaultSettings();
  }

  public getCurrentSettings(): SignSettings {
    return this.signSettings;
  }

  public getDefaultSettings(): SignSettings {
    const sett = new SignSettings();
    const visibleTabs = [SignMethod.FileSystem, SignMethod.Cloud];
    const enabledTabs = [SignMethod.FileSystem, SignMethod.Cloud];
    if (!isMobile || isNativeApp()) {
      visibleTabs.push(SignMethod.Device);
      enabledTabs.push(SignMethod.Device);
    }
    sett.visibleTabs = visibleTabs;
    sett.enabledTabs = enabledTabs;
    return sett;
  }

  public readKey(dlgHeader: string = '', submitBtnText: string = '', isStampRequired: boolean = false): Promise<any> {
    this.signCommand = new ReadKeyCommand();
    let s = this.getDefaultSettings();
    s.dialogHeader = dlgHeader;
    s.submitBtnText = submitBtnText;
    s.isConfirmRequired = false;
    s.isStampAllowed = true;
    s.isShowDocumentFiles = true;
    s.isStampRequired = isStampRequired;
    this.applySettings(s);
    return this.startSigning();
  }

  public signDocumentByAuthor(
    documentVersionCode: string,
    filesForSign: string = '',
    fileNames: string = '',
    doNotShowSignDlg: boolean = false): Promise<any> {
    this.signCommand = new SignDocumentAuthorCommand({ documentVersionCode, filesForSign });
    let s = this.getDefaultSettings();
    s.fileNames = fileNames;
    s.isStampAllowed = true;
    s.isShowDocumentFiles = true;
    this.applySettings(s);
    return this.startSigning(doNotShowSignDlg);
  }

  public signDocumentSignByContragent(
    documentVersionCode: string,
    documentSignUserId: number,
    doNotShowSignDlg: boolean = false,
    isStampRequired: boolean = false): Promise<any> {
    this.signCommand = new SignDocumentContragentCommand({ documentVersionCode, documentSignUserId });
    let s = this.getDefaultSettings();
    s.isStampAllowed = true;
    s.isShowDocumentFiles = true;
    s.isStampRequired = isStampRequired;
    this.applySettings(s);
    return this.startSigning(doNotShowSignDlg);
  }

  public applySignTextSettings(
    signPurpose: SignPurpose,
    signText: string,
    submitBtnText: string = '',
    isConfirmRequired: boolean = true,
    visibleTabs: Array<SignMethod> | null = null,
    showCertExpirationWarning: boolean = true): void {
    this.signCommand = new SignCommand(new SignTextStrategyFactory(), { signText, signPurpose });
    const s = this.getDefaultSettings();
    s.isConfirmRequired = isConfirmRequired;
    s.confirmText = 'appSign.siqnReadQuestion';
    s.submitBtnText = submitBtnText;
    if (visibleTabs !== null) {
      s.visibleTabs = visibleTabs;
    }
    s.showCertExpirationWarning = showCertExpirationWarning;
    this.applySettings(s);
  }

  public signText(
    signPurpose: SignPurpose,
    signText: string,
    submitBtnText: string = '',
    isConfirmRequired: boolean = true,
    visibleTabs: Array<SignMethod> | null = null,
    doNotShowSignDlg: boolean = false): Promise<any> {
    this.applySignTextSettings(signPurpose, signText, submitBtnText, isConfirmRequired, visibleTabs);
    return this.startSigning(doNotShowSignDlg);
  }

  public applyLoginEcpSignTextSettings(signText: string, submitBtnText: string = ''): void {
    this.applySignTextSettings(SignPurpose.Authentication, signText, submitBtnText, false,
      [SignMethod.FileSystem, SignMethod.Device, SignMethod.Cloud], false);
  }

  public applyAddNewCertificateSignTextSettings(signText: string, submitBtnText: string = ''): void {
    this.applySignTextSettings(SignPurpose.AddNewCertificate, signText, submitBtnText, false, null, false);
  }

  public applySignFilesSettings(
    files: Array<any>,
    resultAsBase64: boolean,
    isSignHash: boolean = false,
    isInternal: boolean = false,
    ignoreErrors: boolean = false,
    submitBtnText: string = 'buttons.add',
    visibleTabs: Array<SignMethod> | null = null,
    isConfirmRequired: boolean = true): void {
    this.signCommand = new SignCommand(new SignFilesStrategyFactory(), { files, isSignHash, isInternal, resultAsBase64, ignoreErrors });
    let s = this.getDefaultSettings();
    s.submitBtnText = submitBtnText;
    if (visibleTabs !== null) {
      s.visibleTabs = visibleTabs;
    }
    s.isConfirmRequired = isConfirmRequired;
    this.applySettings(s);
  }

  public signFiles(
    files: Array<any>,
    resultAsBase64: boolean,
    isSignHash: boolean = false,
    isInternal: boolean = false,
    ignoreErrors: boolean = false,
    submitBtnText: string = 'buttons.add',
    visibleTabs: Array<SignMethod> | null = null,
    isConfirmRequired: boolean = true,
    doNotShowSignDlg: boolean = false): Promise<any> {
    this.applySignFilesSettings(files, resultAsBase64, isSignHash, isInternal, ignoreErrors, submitBtnText, visibleTabs, isConfirmRequired);
    return this.startSigning(doNotShowSignDlg);
  }

  public confirmDocumentReceipt(
    documentReceiptConfirmUid: string,
    dialogSubtitleHtml: string = ''): Promise<any> {
    this.signCommand = new SignCommand(
      new ConfirmDocumentReceiptStrategyFactory(),
      { documentReceiptConfirmUid }
    );
    let s = this.getDefaultSettings();
    s.submitBtnText = 'buttons.confirm';
    s.dialogHeader = 'documentReceiptConfirmation.signDlgTitle';
    s.confirmText = 'documentReceiptConfirmation.signDlgConfirm';
    s.isConfirmRequired = true;
    s.dialogSubtitleHtml = dialogSubtitleHtml;
    this.applySettings(s);
    return this.startSigning();
  }

  public signSignaturesRecallProtocol(
    files: Array<any>,
    isStampAllowed: boolean,
    submitBtnText: string = 'buttons.confirm',
    visibleTabs: Array<SignMethod> | null = null): Promise<any> {
    const isSignHash = true;
    const ignoreErrors = false;
    const resultAsBase64 = true;

    this.signCommand = new SignSignaturesRecallProtocolCommand(
      new SignFilesStrategyFactory(),
      { files, isSignHash, resultAsBase64, ignoreErrors });

    let s = this.getDefaultSettings();
    s.isStampAllowed = isStampAllowed;
    s.submitBtnText = submitBtnText;
    if (visibleTabs !== null) {
      s.visibleTabs = visibleTabs;
    }
    s.isConfirmRequired = false;
    s.dialogHeader = 'documentSignRecallDecision.signDlgTitle';
    s.resetReadedKeys = false;
    this.applySettings(s);

    return this.startSigning();
  }

  public async sign(prms?: any): Promise<SignResult> {
    let result = new SignResult();
    try {
      if (!this.signCommand) {
        throw new Error('Sign command is not set');
      }

      const signResult = await this.signCommand.execute(prms);

      this.lastSigningPrms = prms;
      result.isSucceed = true;
      result.result = signResult;

      EventBus.$emit('app-sign-hide-sign-dlg');

      setTimeout(() => { this.resolveSignFn(result); }, 100);
    } catch (error) {
      if (isCancelledError(error)) {
        result.isCancelled = true;
      } else {
        result.isError = true;
        result.error = error;
        result.errorSource = error.errorSource;
      }

      if (this.signSettings.rejectSignPromiseOnError) {
        setTimeout(() => { this.resolveSignFn(result); }, 100);
      }
    }
    return result;
  }

  public cancelSigning() {
    this.resolveSignFn(new SignResult({ isCancelled: true }));
  }

  private startSigning(doNotShowSignDlg: boolean = false): Promise<SignResult> {
    return new Promise(async resolve => {
      try {
        this.resolveSignFn = resolve;

        if (doNotShowSignDlg) {
          const result = await this.sign(this.lastSigningPrms);
          resolve(result);
        } else {
          EventBus.$emit('app-sign-show-sign-dlg');
        }
      } catch (error) {
        resolve(new SignResult({ isError: true, error }));
      }
    });
  }

  private applySettings(settings: SignSettings): void {
    this.signSettings = settings;
  }
}

const signService = new SignService();

export { signService };
